import { Box, Button, Divider, TextField } from "@mui/material";
import { Field, FieldArray, useFormikContext } from "formik";
import { type FC } from "react";
import { EscalationPaymentDetail, type ShortageWorkPacket } from "src/types/work-packets";
import { FormFieldsGroup } from "../components";
import { createFieldFn } from "../field-util";
import { AutocompleteField, DatePickerField } from "components/UI/Form";
import { CurrencyField } from "src/components/UI/Form/CurrencyField";
import {
  getWorkPacketEscalationStatusOption,
  shortageEscalationCaseItemStatusOptions,
} from "src/pages/UserDashboard/WorkPackets/WorkPacketsGrid/helpers/getStaticFilterOptions";
import { ShortagesEscalationCaseItemStatus } from "src/pages/UserDashboard/Cases/api/constants";
import { withCurrentOption } from "src/utils/option-list";

const newPaymentDetail = (caseItemId: string): EscalationPaymentDetail => ({
  id: "",
  caseItemId: caseItemId,
  invoiceId: "",
  invoiceNumber: "",
  disputedInvoiceId: "",
  paidAmount: 0,
  paymentDate: undefined,
  c6InvoiceDate: undefined,
  // TODO: figure out details on Payout Type
  // payoutType: "",
  customerInvoiceAmount: "",
  paymentId: "",
  createdAt: null,
});

const getField = createFieldFn<ShortageWorkPacket>();

export const Escalations: FC = () => {
  const { values: packet } = useFormikContext<ShortageWorkPacket>();

  return packet.escalations.map((escalation, i) => {
    const isItemPaidOut = escalation.status === ShortagesEscalationCaseItemStatus.PAID_OUT;

    return (
      <FormFieldsGroup title={`Escalation ${i + 1}`} withDivider id={`escalation-${i + 1}`} key={escalation.id}>
        <Field
          name={getField(`escalations[${i}].id`)}
          as={TextField}
          variant="outlined"
          size="small"
          label="CG Case ID"
          disabled
        />

        <Field
          name={getField(`escalations[${i}].createdAt`)}
          component={DatePickerField}
          label="Case Created Date"
          disabled
        />

        <Field
          name={getField(`escalations[${i}].status`)}
          component={AutocompleteField}
          label="Case Item Status"
          disabled={isItemPaidOut}
          options={withCurrentOption(
            shortageEscalationCaseItemStatusOptions,
            escalation.status,
            getWorkPacketEscalationStatusOption,
          )}
          getOptionLabel={(option: any) => option.title}
        />

        <Field
          name={getField(`escalations[${i}].remainingOpenBalance`)}
          component={CurrencyField}
          variant="outlined"
          size="small"
          label="Remaining Open Balance"
          disabled
        />

        <Field
          name={getField(`escalations[${i}].monitoringDate`)}
          component={DatePickerField}
          label="Monitoring Date"
          disabled
        />

        <Field
          name={getField(`escalations[${i}].approvedDate`)}
          component={DatePickerField}
          label="Approved Date in Case"
          disabled
        />

        <Field
          name={getField(`escalations[${i}].approvedAmount`)}
          component={CurrencyField}
          variant="outlined"
          size="small"
          label="Approved Amount"
          disabled={isItemPaidOut}
        />

        <FieldArray
          name={getField(`escalations[${i}].paymentDetails`)}
          render={({ push, remove }) => (
            <>
              {escalation.paymentDetails.map((_, j) => (
                <FormFieldsGroup
                  title={`Payment Details ${j + 1}`}
                  id={`escalation-${i + 1}-payment-${j + 1}`}
                  key={`escalation-${i + 1}-payment-${j + 1}`}
                  onRemove={() => {
                    remove(j);
                  }}
                  asSubsection
                >
                  <Field
                    name={getField(`escalations[${i}].paymentDetails[${j}].invoiceId`)}
                    as={TextField}
                    variant="outlined"
                    size="small"
                    label="VC Disputed Invoice"
                  />
                  <Field
                    name={getField(`escalations[${i}].paymentDetails[${j}].invoiceNumber`)}
                    as={TextField}
                    variant="outlined"
                    size="small"
                    label="Invoice # for Payment"
                  />
                  <Field
                    name={getField(`escalations[${i}].paymentDetails[${j}].disputedInvoiceId`)}
                    as={TextField}
                    variant="outlined"
                    size="small"
                    label="Payment ID"
                  />
                  <Field
                    name={getField(`escalations[${i}].paymentDetails[${j}].paidAmount`)}
                    as={TextField}
                    variant="outlined"
                    size="small"
                    label="Paid Amount"
                  />
                  <Field
                    name={getField(`escalations[${i}].paymentDetails[${j}].paymentDate`)}
                    component={DatePickerField}
                    label="Payment Date"
                  />
                  <Field
                    name={getField(`escalations[${i}].paymentDetails[${j}].c6InvoiceDate`)}
                    component={DatePickerField}
                    label="C6 Invoiced Date"
                  />
                  {/* TODO: figure out details on Payout Type */}
                  {/* <Field
                name={getField(`escalations[${i}].paymentDetails[${j}].payoutType`)}
                as={TextField}
                variant="outlined"
                size="small"
                label="Payout Type"
                 /> */}
                </FormFieldsGroup>
              ))}
              <Box style={{ gridColumn: "1 / -1", textAlign: "center" }} sx={{ mb: 2 }}>
                <Divider sx={{ mt: 0, mb: 2 }} />
                <Button
                  variant="outlined"
                  onClick={() => {
                    push(newPaymentDetail(escalation.itemId));
                  }}
                >
                  Add Another Payment Details
                </Button>
              </Box>
            </>
          )}
        />
      </FormFieldsGroup>
    );
  });
};
